/**
 * V325.22: Shared Cloudinary URL canonicalization
 * Ensures all components derive transforms from the same base URL.
 * Used by both UserAvatar (Chat) and UserPhoto (Network/Profile).
 */

/**
 * Check if URL is a Cloudinary image URL
 */
export function isCloudinaryUrl(url: string): boolean {
  return (
    typeof url === 'string' &&
    url.includes('res.cloudinary.com') &&
    url.includes('/image/upload/')
  );
}

/**
 * Normalize any Cloudinary URL to its canonical base form:
 *   https://res.cloudinary.com/{cloud}/image/upload/v{version}/{path}
 *
 * Strips any existing transforms like /upload/w_64,h_64,c_fill,.../v123/...
 * so that getOptimizedImageUrl always produces clean, predictable output.
 *
 * Non-Cloudinary URLs (blob:, data:, external) are returned unchanged.
 */
export function normalizeCloudinaryBaseUrl(url: string): string {
  if (!isCloudinaryUrl(url)) return url;

  const marker = '/image/upload/';
  const i = url.indexOf(marker);
  if (i === -1) return url;

  const prefix = url.slice(0, i + marker.length);
  const rest = url.slice(i + marker.length);

  // Already base form (starts with v123/)
  if (/^v\d+\//.test(rest)) return url;

  // Strip transforms: find first "/v123/" segment
  const m = rest.match(/\/v\d+\//);
  if (!m || m.index == null) return url; // unexpected format, return as-is

  return prefix + rest.slice(m.index + 1);
}
